import { useEffect } from 'react';
import { Progress } from '@backstage/core-components';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';

// --- GuestUserIdentity ---
class GuestUserIdentity {
  getUserId() {
    return 'guest';
  }
  async getIdToken() {
    return undefined;
  }
  getProfile() {
    return {
      email: 'guest@example.com',
      displayName: 'Guest',
    };
  }
  async getProfileInfo() {
    return {
      email: 'guest@example.com',
      displayName: 'Guest',
    };
  }
  async getBackstageIdentity() {
    const userEntityRef = 'user:default/guest';
    return {
      type: 'user',
      userEntityRef,
      ownershipEntityRefs: [userEntityRef],
    };
  }
  async getCredentials() {
    return {};
  }
  async signOut() {}
}

// --- ProxiedSignInIdentity ---
interface ProxiedSession {
  backstageIdentity: {
    token: string;
    identity: {
      type: string;
      userEntityRef: string;
      ownershipEntityRefs: string[];
    };
  };
  profile: {
    email: string;
    displayName: string;
  };
}

type ProxiedState =
  | { type: 'empty' }
  | { type: 'active'; session: ProxiedSession; expiresAt: Date }
  | {
      type: 'fetching';
      promise: Promise<ProxiedSession>;
      previous?: ProxiedSession;
    }
  | { type: 'failed'; error: any };

function tokenToExpiry(jwtToken: string): Date {
  const fallback = new Date(Date.now() + 5 * 60 * 1000);
  if (!jwtToken) {
    return fallback;
  }
  const [_header, rawPayload, _signature] = jwtToken.split('.');
  const payload = JSON.parse(window.atob(rawPayload));
  if (typeof payload.exp !== 'number') {
    return fallback;
  }
  return new Date(payload.exp * 1000 - 5 * 60 * 1000);
}

class ProxiedSignInIdentity {
  options: any;
  abortController: AbortController;
  state: ProxiedState;
  constructor(options: any) {
    this.options = options;
    this.abortController = new AbortController();
    this.state = { type: 'empty' };
  }
  async start() {
    await this.getSessionAsync();
  }
  getUserId() {
    const { backstageIdentity } = this.getSessionSync();
    const ref = backstageIdentity.identity.userEntityRef;
    const match = /^([^:/]+:)?([^:/]+\/)?([^:/]+)$/.exec(ref);
    if (!match) {
      throw new TypeError(`Invalid user entity reference "${ref}"`);
    }
    return match[3];
  }
  async getIdToken() {
    const session = await this.getSessionAsync();
    return session.backstageIdentity.token;
  }
  getProfile() {
    const session = this.getSessionSync();
    return session.profile;
  }
  async getProfileInfo() {
    const session = await this.getSessionAsync();
    return session.profile;
  }
  async getBackstageIdentity() {
    const session = await this.getSessionAsync();
    return session.backstageIdentity.identity;
  }
  async getCredentials() {
    const session = await this.getSessionAsync();
    return {
      token: session.backstageIdentity.token,
    };
  }
  async signOut() {
    this.abortController.abort();
  }
  getSessionSync(): ProxiedSession {
    if (this.state.type === 'active') {
      return this.state.session;
    } else if (this.state.type === 'fetching' && this.state.previous) {
      return this.state.previous;
    }
    throw new Error('No session available. Try reloading your browser page.');
  }
  async getSessionAsync(): Promise<ProxiedSession> {
    if (this.state.type === 'fetching') {
      return this.state.promise;
    } else if (
      this.state.type === 'active' &&
      new Date() < this.state.expiresAt
    ) {
      return this.state.session;
    }
    const previous =
      this.state.type === 'active' ? this.state.session : undefined;
    const promise = this.fetchSession().then(
      (session: ProxiedSession) => {
        this.state = {
          type: 'active',
          session,
          expiresAt: tokenToExpiry(session.backstageIdentity.token),
        };
        return session;
      },
      (error: any) => {
        this.state = {
          type: 'failed',
          error,
        };
        throw error;
      },
    );
    this.state = {
      type: 'fetching',
      promise,
      previous,
    };
    return promise;
  }
  async fetchSession(): Promise<ProxiedSession> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl('auth');
    const headers =
      typeof this.options.headers === 'function'
        ? await this.options.headers()
        : this.options.headers;
    const mergedHeaders = new Headers(headers);
    mergedHeaders.set('X-Requested-With', 'XMLHttpRequest');
    const response = await fetch(
      `${baseUrl}/${this.options.provider}/refresh`,
      {
        signal: this.abortController.signal,
        headers: mergedHeaders,
        credentials: 'include',
      },
    );
    if (!response.ok) {
      throw new Error('Failed to fetch guest session');
    }
    return await response.json();
  }
}

// --- CustomSignInPage ---
const getIdentity = async (identity: any) => {
  try {
    const identityResponse = await identity.getBackstageIdentity();
    return identityResponse;
  } catch (error: any) {
    if (
      error.name === 'ResponseError' &&
      error.cause?.name === 'NotFoundError'
    ) {
      return undefined;
    }
    throw error;
  }
};

const GuestSignInPage = ({
  onSignInSuccess,
}: {
  onSignInSuccess: (identityApi: any) => void;
}) => {
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const identity = new ProxiedSignInIdentity({
        provider: 'guest',
        discoveryApi,
      });
      const identityResponse = await getIdentity(identity).catch(
        () => undefined,
      );
      if (mounted) {
        if (identityResponse) {
          onSignInSuccess(identity);
        } else {
          onSignInSuccess(new GuestUserIdentity());
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [discoveryApi, onSignInSuccess]);

  return <Progress />;
};

export default GuestSignInPage;
